import { invalidateClientPerms, useClientPerms } from "../lib/useClientPerms";
import { groupAreas } from "../components/StaffPermissions";
import { useRoles } from "../lib/useRoles";
import { RoleManager } from "../components/RoleManager";
import { UserManualPanel } from "../components/UserManualPanel";
import { NotificationReportPanel } from "../components/NotificationReportPanel";
import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { setBrandCache, DEFAULT_TIMEZONE } from "../lib/useBrand";
import { fmtDateTime, TIMEZONES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Radio, Save, RotateCcw, ShieldCheck, Image as ImageIcon, Upload, UserCog, Tags, Bell, BookOpen, Landmark } from "lucide-react";
import { PostWageAccountPanel } from "../components/PostWageAccountPanel";
import { ForteEnvironmentCard } from "../components/ForteEnvironmentCard";
import Notifications from "./Notifications";
import { StaffPanel } from "./Staff";
import DataImportPanel from "../components/DataImportPanel";
import PayrollCodesPanel from "../components/PayrollCodesPanel";
import PayrollHistoryImport from "../components/PayrollHistoryImport";
import { AccountSecurityPanel } from "../components/AccountSecurity";
import { RolePreviewControl, AccessDeniedLog } from "../components/ClientPermsTools";

const AREA_LABELS = {
  company: "Company Profile", employees: "Employees", time: "Time", people: "People", run_payroll: "Run Payroll",
  history: "Pay History", remittance: "Remittance", reports: "Reports", t4: "T4 Slips", migration: "Migration", notifications: "Settings › Notifications",
};
const CLIENT_AREA_GROUPS = [
  ["Client workspace", ["company", "employees", "time", "people", "run_payroll", "history", "remittance", "reports", "t4", "migration"]],
  ["Settings (client portal)", ["notifications"]],
];
function ClientPermsPanel() {
  const roles = useRoles();
  const CLIENT_ROLES = roles.client.map((r) => [r.key, r.label]);
  const [areas, setAreas] = useState([]);
  const [perms, setPerms] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = !!perms && JSON.stringify(perms) !== JSON.stringify(saved);

  const load = () => api.get("/settings/client-permissions").then((r) => { setAreas(r.data.areas || []); setPerms(r.data.permissions || {}); setSaved(r.data.permissions || {}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (role, area, key) => {
    setPerms((p) => ({ ...p, [role]: { ...p[role], [area]: { ...p[role][area], [key]: !p[role][area][key] } } }));
  };

  const reset = async () => {
    if (!window.confirm("Reset the client permissions matrix to the standard defaults? This replaces your saved customizations.")) return;
    setBusy(true);
    try {
      const r = await api.post("/settings/client-permissions/reset");
      invalidateClientPerms();
      setPerms(r.data.permissions); setSaved(r.data.permissions);
      toast.success("Client permissions reset to defaults");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/client-permissions", { permissions: perms });
      invalidateClientPerms();
      setPerms(r.data.permissions); setSaved(r.data.permissions);
      toast.success("Client permissions saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!perms) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="border border-border bg-white p-6">
      <h2 className="font-bold tracking-tight">Client portal roles & permissions</h2>
      <p className="text-xs text-muted-foreground mt-1">Firm-wide. Control what each client sub-role can view and edit in their portal. Applies to every client company.</p>
      <div className="mt-5"><RoleManager kind="client" onChanged={load} /></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-border">
          <thead>
            <tr className="bg-neutral-50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="text-left px-4 py-2 border-b border-border">Area</th>
              {CLIENT_ROLES.map(([rk, rl]) => (
                <th key={rk} className="px-4 py-2 border-b border-l border-border text-center" colSpan={2}>{rl}</th>
              ))}
            </tr>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-1.5 border-b border-border"></th>
              {CLIENT_ROLES.map(([rk]) => (
                <React.Fragment key={rk}>
                  <th className="px-2 py-1.5 border-b border-l border-border text-center">View</th>
                  <th className="px-2 py-1.5 border-b border-border text-center">Edit</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupAreas(areas, CLIENT_AREA_GROUPS).map(([group, ids]) => [
              <tr key={"g-" + group} data-testid={`perm-group-${group.replace(/\W+/g, "-").toLowerCase()}`} className="bg-neutral-100/80"><td colSpan={1 + CLIENT_ROLES.length * 2} className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{group}</td></tr>,
              ...ids.map((a) => (
              <tr key={a} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5 font-medium">{AREA_LABELS[a] || a}</td>
                {CLIENT_ROLES.map(([rk]) => (
                  <React.Fragment key={rk}>
                    <td className="px-2 py-2.5 border-l border-border text-center">
                      <input type="checkbox" data-testid={`perm-${rk}-${a}-view`} checked={!!perms[rk]?.[a]?.view} onChange={() => toggle(rk, a, "view")} className="h-4 w-4 accent-black" />
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <input type="checkbox" data-testid={`perm-${rk}-${a}-edit`} checked={!!perms[rk]?.[a]?.edit} onChange={() => toggle(rk, a, "edit")} className="h-4 w-4 accent-black" />
                    </td>
                  </React.Fragment>
                ))}
              </tr>
              ))])}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">Client portal users can never delete records, regardless of role — deletions are a Postings Inc staff action.</p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button data-testid="save-client-perms" disabled={busy || !dirty} onClick={save} className="inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save permissions"}
        </button>
        <button data-testid="reset-client-perms" disabled={busy} onClick={reset} className="inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 text-sm font-medium hover:border-black transition-colors disabled:opacity-50">
          <RotateCcw className="h-4 w-4" /> Reset to defaults
        </button>
        {dirty && <span data-testid="client-perms-unsaved" className="text-xs text-amber-700">Unsaved changes</span>}
      </div>
      <RolePreviewControl perms={perms} dirty={dirty} />
      <AccessDeniedLog />
    </div>
  );
}

const EMPTY = { number: "", name: "", contact: "", area_code: "", phone: "", email: "" };

function TransmitterPanel({ isAdmin }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/transmitter").then((r) => setForm({ ...EMPTY, ...(r.data || {}) })).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/transmitter", form);
      toast.success("Transmitter details saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-white p-6 w-full">
      <h2 className="font-bold tracking-tight">Transmitter identification</h2>
      <p className="text-xs text-muted-foreground mt-1">
        Your CRA transmitter number and contact details are embedded in the T619 header of every T4 XML e-filing.
        Format is <span className="font-mono">MM</span> followed by 6 digits (e.g. <span className="font-mono">MM555555</span>).
      </p>

      <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Field label="Transmitter number (MM number)" testid="transmitter-number" value={form.number} onChange={(v) => setForm({ ...form, number: v })} placeholder="MM555555" disabled={!isAdmin} />
        <Field label="Transmitter name" testid="transmitter-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Postings Inc" disabled={!isAdmin} />
        <Field label="Contact name" testid="transmitter-contact" value={form.contact} onChange={(v) => setForm({ ...form, contact: v })} placeholder="Payroll Administrator" disabled={!isAdmin} />
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <Field label="Area code" testid="transmitter-area" value={form.area_code} onChange={(v) => setForm({ ...form, area_code: v })} placeholder="416" disabled={!isAdmin} />
          <Field label="Phone" testid="transmitter-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="555-0100" disabled={!isAdmin} />
        </div>
        <Field label="Contact email" testid="transmitter-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="payroll@postingsinc.ca" disabled={!isAdmin} />
      </div>

      {isAdmin ? (
        <button data-testid="save-transmitter-button" disabled={busy} onClick={save} className="mt-6 inline-flex items-center gap-2 bg-black text-white px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save transmitter"}
        </button>
      ) : (
        <p className="mt-6 text-xs text-muted-foreground">Only Postings Inc administrators can edit transmitter settings.</p>
      )}
    </div>
  );
}

function BrandPanel({ isMaster }) {
  const [logo, setLogo] = useState("");
  const [loginLogo, setLoginLogo] = useState("");
  const [favicon, setFavicon] = useState("");
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [busy, setBusy] = useState(false);
  const EMAIL_KEYS = ["firm_name", "firm_phone", "firm_website", "payroll_email", "portal_label", "portal_url"];
  const [emailBlock, setEmailBlock] = useState({});
  const [emailDefaults, setEmailDefaults] = useState({});

  useEffect(() => {
    api.get("/settings/brand").then((r) => {
      setLogo(r.data?.logo || "");
      setLoginLogo(r.data?.login_logo || "");
      setFavicon(r.data?.favicon || "");
      setName(r.data?.name || "");
      setTimezone(r.data?.timezone || DEFAULT_TIMEZONE);
      setEmailBlock(Object.fromEntries(EMAIL_KEYS.map((k) => [k, r.data?.[k] || ""])));
      setEmailDefaults(r.data?.email_defaults || {});
    }).catch(() => {});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (next) => {
    const payload = { logo, login_logo: loginLogo, favicon, name, timezone, ...emailBlock, ...next };
    setBusy(true);
    try {
      await api.put("/settings/brand", payload);
      setLogo(payload.logo);
      setLoginLogo(payload.login_logo);
      setFavicon(payload.favicon);
      setName(payload.name);
      setTimezone(payload.timezone);
      setBrandCache(payload);
      toast.success("Branding saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const readFile = (e, cb, maxKb = 400) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxKb * 1024) { toast.error(`Please use an image under ${maxKb} KB.`); return; }
    const reader = new FileReader();
    reader.onload = () => cb(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="grid xl:grid-cols-2 gap-6 w-full items-start">
      <div className="border border-border bg-white p-6">
        <h2 className="font-bold tracking-tight">Firm logo</h2>
        <p className="text-xs text-muted-foreground mt-1">Shown across the console and app (sidebar, headers). Use a PNG/JPG under 400 KB, or paste a public image URL.</p>
        <div className="mt-6 flex items-center gap-4">
          {logo ? (
            <img src={logo} alt="brand" data-testid="brand-logo-preview" className="h-16 w-auto max-w-[180px] object-contain border border-border bg-white p-1.5" />
          ) : (
            <div className="h-16 w-16 bg-black text-white flex items-center justify-center font-black text-2xl">P</div>
          )}
          {isMaster && (
            <div className="flex-1 space-y-2">
              <input data-testid="brand-name" value={name} placeholder="Firm name (shown beside the logo)…" onChange={(e) => setName(e.target.value)} onBlur={(e) => save({ name: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-black" />
              <input data-testid="brand-logo-url" value={(logo || "").startsWith("data:") ? "" : (logo || "")} placeholder="Paste a public image URL…" onChange={(e) => setLogo(e.target.value)} onBlur={(e) => save({ logo: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <div className="flex items-center gap-3">
                <label data-testid="brand-logo-upload" className="cursor-pointer inline-flex items-center gap-2 border border-border bg-white px-3 py-2 text-xs font-medium hover:border-black transition-colors">
                  <ImageIcon className="h-3.5 w-3.5" /> Upload logo
                  <input type="file" accept="image/*" onChange={(e) => readFile(e, (v) => save({ logo: v }))} className="hidden" />
                </label>
                {logo && <button data-testid="brand-logo-remove" disabled={busy} onClick={() => save({ logo: "" })} className="text-xs text-red-600 hover:underline">Remove logo</button>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border border-border bg-white p-6">
        <h2 className="font-bold tracking-tight">Sign-in screen logo</h2>
        <p className="text-xs text-muted-foreground mt-1">A larger, more prominent logo shown only on the login page. If left empty, the firm logo above is used.</p>
        <div className="mt-6 flex items-center gap-4">
          {(loginLogo || logo) ? (
            <img src={loginLogo || logo} alt="login brand" data-testid="brand-login-logo-preview" className="h-24 w-auto max-w-[240px] object-contain border border-border bg-white p-2" />
          ) : (
            <div className="h-24 w-24 bg-black text-white flex items-center justify-center font-black text-3xl">P</div>
          )}
          {isMaster && (
            <div className="flex-1 space-y-2">
              <input data-testid="brand-login-logo-url" value={(loginLogo || "").startsWith("data:") ? "" : (loginLogo || "")} placeholder="Paste a public image URL…" onChange={(e) => setLoginLogo(e.target.value)} onBlur={(e) => save({ login_logo: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <div className="flex items-center gap-3">
                <label data-testid="brand-login-logo-upload" className="cursor-pointer inline-flex items-center gap-2 border border-border bg-white px-3 py-2 text-xs font-medium hover:border-black transition-colors">
                  <ImageIcon className="h-3.5 w-3.5" /> Upload sign-in logo
                  <input type="file" accept="image/*" onChange={(e) => readFile(e, (v) => save({ login_logo: v }))} className="hidden" />
                </label>
                {loginLogo && <button data-testid="brand-login-logo-remove" disabled={busy} onClick={() => save({ login_logo: "" })} className="text-xs text-red-600 hover:underline">Remove</button>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border border-border bg-white p-6">
        <h2 className="font-bold tracking-tight">Favicon</h2>
        <p className="text-xs text-muted-foreground mt-1">The small icon shown in the browser tab and bookmarks. Use a square .ico, .png or .svg (32×32 or 64×64 recommended, under 100 KB). Applies to the console, sign-in page and client portal.</p>
        <div className="mt-6 flex items-center gap-4">
          <div className="flex items-center gap-2 border border-border bg-neutral-100 px-3 py-2">
            <img src={favicon || `${process.env.PUBLIC_URL || ""}/favicon.ico`} alt="favicon" data-testid="brand-favicon-preview" className="h-4 w-4 object-contain" />
            <span className="text-xs text-muted-foreground truncate max-w-[160px]">{name || "PostWage"}</span>
          </div>
          {isMaster && (
            <div className="flex items-center gap-3">
              <label data-testid="brand-favicon-upload" className="cursor-pointer inline-flex items-center gap-2 border border-border bg-white px-3 py-2 text-xs font-medium hover:border-black transition-colors">
                <ImageIcon className="h-3.5 w-3.5" /> Upload favicon
                <input type="file" accept=".ico,.png,.svg,image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml" onChange={(e) => readFile(e, (v) => save({ favicon: v }), 100)} className="hidden" />
              </label>
              {favicon && <button data-testid="brand-favicon-remove" disabled={busy} onClick={() => save({ favicon: "" })} className="text-xs text-red-600 hover:underline">Remove (use default)</button>}
            </div>
          )}
        </div>
      </div>

      <div className="border border-border bg-white p-6" data-testid="brand-timezone-block">
        <h2 className="font-bold tracking-tight">Time zone</h2>
        <p className="text-xs text-muted-foreground mt-1">Every clock time in the app (draft saves, email receipts, sign-ins, activity) is shown in this zone. Default is Eastern.</p>
        <select data-testid="brand-timezone" value={timezone} disabled={!isMaster || busy}
          onChange={(e) => { setTimezone(e.target.value); save({ timezone: e.target.value }); }}
          className="mt-4 w-full sm:max-w-sm border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-50">
          {TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-2" data-testid="brand-timezone-now">Current time: <span className="font-mono">{fmtDateTime(new Date().toISOString())}</span></p>
      </div>

      <div className="border border-border bg-white p-6" data-testid="brand-email-block">
        <h2 className="font-bold tracking-tight">Client email signature &amp; portal</h2>
        <p className="text-xs text-muted-foreground mt-1">Used in the "Payroll Processed – Confirmation" (Payroll History › Run) and "PD7A Processed – Confirmation" (Remittance) emails. Leave a field blank to use the default shown.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {[["firm_name", "Firm name"], ["firm_phone", "Phone"], ["firm_website", "Website"], ["payroll_email", "Payroll team email"], ["portal_label", "Client portal link text"], ["portal_url", "Client portal URL (blank = this app's sign-in)"]].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
              <input data-testid={`brand-${k.replace(/_/g, "-")}`} value={emailBlock[k] || ""} placeholder={emailDefaults[k] || ""} disabled={!isMaster}
                onChange={(e) => setEmailBlock((b) => ({ ...b, [k]: e.target.value }))} onBlur={(e) => save({ [k]: e.target.value })}
                className="mt-1 w-full border border-input bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-50" />
            </label>
          ))}
        </div>
      </div>

      {!isMaster && <p className="text-xs text-muted-foreground">Only Postings Inc administrators can change the firm branding.</p>}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const isMaster = isAdmin && (user?.staff_role || "master") === "master";
  const { canView, canEdit, loaded } = useClientPerms();   // staff matrix: Settings-tab view/edit per role (master unrestricted)
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "");

  const groups = [
    { label: "Firm", items: [
      { id: "branding", label: "Branding", icon: ImageIcon, show: isAdmin && canView("settings_branding") },
      { id: "transmitter", label: "CRA Transmitter (T619)", icon: Radio, show: isAdmin && canView("settings_transmitter") },
      { id: "payments", label: "Payments (PostWage bank)", icon: Landmark, show: isMaster },
    ] },
    { label: "People & Access", items: [
      { id: "users", label: "Users & Permissions", icon: ShieldCheck, show: isMaster,
        sub: [{ id: "staff", label: "Staff & Roles" }, { id: "client_perms", label: "Client Permissions" }] },
      { id: "account", label: "Account & Security", icon: UserCog, show: true },
    ] },
    { label: "Communication", items: [
      { id: "notifications", label: "Notifications", icon: Bell, show: isAdmin && canView("settings_notifications"),
        sub: [{ id: "notifications", label: "Settings" }, { id: "email_report", label: "Email Report", show: isMaster }] },
    ] },
    { label: "Payroll Data", items: [
      { id: "codes", label: "Earning & Deduction Codes", icon: Tags, show: isAdmin && canView("codes") },
      { id: "import", label: "Data Import", icon: Upload, show: isAdmin && (canView("data_import") || canView("settings_payroll_history")),
        sub: [{ id: "import", label: "Employees & Time", show: canView("data_import") }, { id: "payroll_history", label: "Payroll History", show: canView("settings_payroll_history") }] },
    ] },
    { label: "Help", items: [
      { id: "manual", label: "User Manual (AI)", icon: BookOpen, show: isMaster },
    ] },
  ].map((g) => ({ ...g, items: g.items.filter((t) => t.show).map((t) => ({ ...t, sub: (t.sub || []).filter((s) => s.show !== false) })) })).filter((g) => g.items.length);
  const items = groups.flatMap((g) => g.items);
  const parentOf = (id) => items.find((t) => t.id === id || t.sub.some((s) => s.id === id));
  const parent = parentOf(tab) || items[0];
  const active = parent?.sub.length ? (parent.sub.some((s) => s.id === tab) ? tab : parent.sub[0].id) : parent?.id;
  if (!loaded) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-4xl font-black tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">Postings Inc configuration</p>
      </div>

      <div className="flex flex-col md:flex-row gap-6 lg:gap-10">
        <nav data-testid="settings-side-menu" className="md:w-60 shrink-0 md:border-r border-border md:pr-4 flex md:flex-col gap-4 md:gap-5 overflow-x-auto md:overflow-visible">
          {groups.map((g) => (
            <div key={g.label} className="shrink-0">
              <div className="hidden md:block text-[10px] font-mono uppercase tracking-widest text-muted-foreground px-3 mb-1.5">{g.label}</div>
              <div className="flex md:flex-col gap-1">
                {g.items.map((t) => (
                  <button key={t.id} data-testid={`settings-tab-${t.id}`} onClick={() => setTab(t.sub.length ? t.sub[0].id : t.id)} aria-current={parent?.id === t.id ? "page" : undefined}
                    className={`flex items-center gap-2.5 px-3 py-2 text-sm text-left whitespace-nowrap transition-colors ${parent?.id === t.id ? "bg-black text-white font-medium" : "text-muted-foreground hover:text-black hover:bg-neutral-100"}`}>
                    <t.icon className="h-4 w-4 shrink-0" /> {t.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-1 min-w-0">
          {parent?.sub.length > 1 && (
            <div className="flex gap-1 border-b border-border mb-6" data-testid="settings-subtabs">
              {parent.sub.map((s) => (
                <button key={s.id} data-testid={`settings-tab-${s.id}`} onClick={() => setTab(s.id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${active === s.id ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {active === "transmitter" && <TransmitterPanel isAdmin={isAdmin && canEdit("settings_transmitter")} />}
          {active === "payments" && isMaster && <><ForteEnvironmentCard /><PostWageAccountPanel /></>}
          {active === "branding" && <BrandPanel isMaster={isAdmin && canEdit("settings_branding")} />}
          {active === "notifications" && <Notifications embedded />}
          {active === "email_report" && isMaster && <NotificationReportPanel />}
          {active === "client_perms" && isMaster && <ClientPermsPanel />}
          {active === "staff" && isMaster && <StaffPanel />}
          {active === "import" && <DataImportPanel />}
          {active === "payroll_history" && <PayrollHistoryImport />}
          {active === "codes" && <PayrollCodesPanel />}
          {active === "manual" && isMaster && <UserManualPanel />}
          {active === "account" && <div className="max-w-lg"><AccountSecurityPanel /></div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, disabled }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testid} value={value} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100 disabled:text-neutral-500" />
    </div>
  );
}
